import { CHARACTER_SCHEMA_VERSION } from './character.ts';
import { emptyHotlist } from '../rules/hotlist.ts';

/**
 * Upgrades an older saved character record to the current schema.
 *
 * Each step takes a record at version N and returns it at version N+1.
 * Add new steps to the end. NEVER edit a step that already exists, or
 * previously saved characters will stop loading.
 *
 * Migrations must be conservative: fill new fields with the value the rules
 * would have produced, and never discard data.
 */
export type CharacterMigration = (record: Record<string, unknown>) => Record<string, unknown>;

/** 1 -> 2: added Health Bar state, Mana, AI Favor, size and Dying state. */
const migrate1to2: CharacterMigration = (record) => {
  const stats = (record['unenhancedStats'] ?? {}) as Record<string, number>;
  const intelligence = typeof stats['int'] === 'number' ? stats['int'] : 0;
  const crawlerType = record['crawlerType'];

  return {
    ...record,
    // A crawler with no recorded damage starts on a full Health Bar.
    healthBarSlotsRemaining: 10,
    currentMana: intelligence,
    aiFavor: crawlerType === 'human' ? 1 : 0,
    size: 4,
    dyingRoundsRemaining: null,
  };
};

/** 2 -> 3: added the record of background and starting-combat choices. */
const migrate2to3: CharacterMigration = (record) => ({ ...record, origin: null });

/** 3 -> 4: added Skill advancement marks. A migrated crawler starts unmarked. */
const migrate3to4: CharacterMigration = (record) => ({ ...record, advancementMarks: [] });

/** 4 -> 5: added the change log. Existing crawlers start with an empty one. */
const migrate4to5: CharacterMigration = (record) => ({ ...record, changeLog: [] });

/**
 * 5 -> 6: a change log entry's `undo` became a LIST of changes instead of one.
 *
 * At schema 5 the inverse could not express a change that touched two fields,
 * so removing a marked Skill did not restore its advancement mark on undo. The
 * fix is architectural (see `inverseOf` in `src/rules/characterChanges.ts`);
 * this step only reshapes what is already saved.
 *
 * Old entries are wrapped as they stand. That is deliberate: a schema-5 entry
 * recorded whatever inverse the old code produced, and re-deriving a better one
 * is impossible - the state it would need is gone. Old entries therefore stay
 * exactly as reversible as they always were, and every entry written from now
 * on is correct. Nothing is discarded.
 */
const migrate5to6: CharacterMigration = (record) => {
  const log = record['changeLog'];
  if (!Array.isArray(log)) return { ...record, changeLog: [] };

  return {
    ...record,
    changeLog: log.map((entry) => {
      if (typeof entry !== 'object' || entry === null) return entry;
      const e = entry as Record<string, unknown>;
      if (Array.isArray(e['undo'])) return e;
      // A missing inverse becomes an empty list, which reverses to a no-op,
      // rather than an entry that would throw when someone presses undo.
      return { ...e, undo: e['undo'] === undefined || e['undo'] === null ? [] : [e['undo']] };
    }),
  };
};

/**
 * 6 -> 7: inventory, and creation grants become real Items.
 *
 * Before this, `origin.grants` was a list of STRINGS - "5 Standard Mana
 * Potions", "New Achievement for Unarmed Combat, and a Bronze Weapon Box" -
 * written when there was no item system to point at. They are matched back to
 * the Items they always described, and become BOTH:
 *
 *  - `origin.grants`, now `{ itemId, quantity }`, the historical record of what
 *    creation handed over; and
 *  - ordinary inventory entries, indistinguishable from the same item picked up
 *    later, which is the whole point.
 *
 * A grant string that matches nothing is DROPPED from `origin.grants` rather
 * than guessed at, and is recorded verbatim in `origin.unmatchedGrants` so the
 * history is not destroyed. Inventing an item ID for a string nobody can parse
 * would be worse than saying plainly that it was not understood.
 */
const migrate6to7: CharacterMigration = (record) => {
  const origin = record['origin'];
  const inventory: { itemId: string; quantity: number; equipped: number; held: number }[] = [];

  if (typeof origin !== 'object' || origin === null) {
    return { ...record, inventory };
  }

  const o = { ...(origin as Record<string, unknown>) };
  const oldGrants = Array.isArray(o['grants']) ? o['grants'] : [];

  const matched: { itemId: string; quantity: number }[] = [];
  const unmatched: string[] = [];

  for (const grant of oldGrants) {
    if (typeof grant === 'object' && grant !== null && 'itemId' in grant) {
      // Already migrated, or written by a build between these versions.
      matched.push(grant as { itemId: string; quantity: number });
      continue;
    }
    if (typeof grant !== 'string') continue;
    const found = matchGrantString(grant);
    if (found) matched.push(found);
    else unmatched.push(grant);
  }

  for (const grant of matched) {
    const existing = inventory.find((e) => e.itemId === grant.itemId);
    if (existing) existing.quantity += grant.quantity;
    else inventory.push({ itemId: grant.itemId, quantity: grant.quantity, equipped: 0, held: 0 });
  }

  o['grants'] = matched;
  // Always present, so the field is real schema rather than something that
  // only appears when it happens to be needed.
  o['unmatchedGrants'] = unmatched;

  // Version 6 Backgrounds granted no Items, so every entry gets an empty list.
  const backgrounds = Array.isArray(o['backgrounds']) ? o['backgrounds'] : [];
  o['backgrounds'] = backgrounds.map((entry) =>
    typeof entry === 'object' && entry !== null && !('grantedItems' in entry)
      ? { ...(entry as Record<string, unknown>), grantedItems: [] }
      : entry,
  );

  return { ...record, origin: o, inventory };
};

/**
 * Matches a version 6 grant STRING to the Item it described.
 *
 * Deliberately narrow: it recognises only the two grants the supplied books
 * actually specify (C&C p.13), which are the only two the application ever
 * wrote. Anything else is reported as unmatched rather than parsed loosely.
 */
function matchGrantString(text: string): { itemId: string; quantity: number } | null {
  const lower = text.toLowerCase();
  if (lower.includes('mana potion')) {
    const count = /(\d+)\s+standard mana potion/.exec(lower);
    return { itemId: 'standard-mana-potion', quantity: count ? Number(count[1]) : 1 };
  }
  if (lower.includes('weapon box')) {
    return { itemId: 'bronze-weapon-box', quantity: 1 };
  }
  return null;
}

/**
 * 7 -> 8: added the ten-slot Hotlist. Every existing crawler gets an empty one,
 * which is correct - none of them had a Hotlist to lose.
 *
 * CANONICALISED AFTER THE SPREAD, deliberately. A version 7 record could not
 * carry a Hotlist, so one that has a `hotlist` field is hand-edited or corrupt.
 * Spreading last would let it keep whatever it smuggled in, and the ten-slot
 * capacity rule would be bypassed before any check ever ran. This is the same
 * lesson as portable migration 3 -> 4.
 */
const migrate7to8: CharacterMigration = (record) => ({ ...record, hotlist: emptyHotlist() });

/**
 * 8 -> 9: a Hotlist ITEM slot records how many copies it holds.
 *
 * RULE DCC-HOT-019 - C&C p.15 puts up to 999 of one item in a single slot, so a
 * slot is an allocation of physical copies rather than a bare pointer. Schema 8
 * shipped for one review cycle with no count.
 *
 * EXISTING ENTRIES BECOME 1, never "all owned copies". Guessing the larger
 * number would silently allocate a crawler's whole stack to the Hotlist. One
 * copy preserves the shortcut and claims the least.
 *
 * IT DOES NOT GUARANTEE THE INVARIANT HOLDS, and it must not claim to. A
 * schema 8 shortcut could perfectly well have named the one weapon the crawler
 * was already wielding, because schema 8 had no count and therefore no
 * allocation rule. Migration will then produce owned 1 / equipped 1 /
 * hotlisted 1, which breaks the allocation invariant - `equipped + hotlisted
 * <= owned` as it stood at schema 9, and `equipped + held + hotlisted <= owned`
 * since E5B added the third term.
 *
 * That is ACCEPTABLE, and only because of how such a state is treated: it is an
 * ownership over-allocation, so it loads, shows as unavailable, and does
 * nothing until the player clears the slot or finds another copy. What it must
 * NOT do is silently drop the shortcut to make the numbers work - that would
 * delete something the player chose in order to tidy up after a schema change
 * they never asked for.
 *
 * Spell entries are untouched: a Spell is not a physical object and has nothing
 * to count.
 */
const migrate8to9: CharacterMigration = (record) => {
  const hotlist = record['hotlist'];
  if (!Array.isArray(hotlist)) return { ...record, hotlist: emptyHotlist() };
  return {
    ...record,
    hotlist: hotlist.map((slot) => {
      if (typeof slot !== 'object' || slot === null) return slot;
      const entry = slot as Record<string, unknown>;
      if (entry['kind'] !== 'item') return entry;
      // Canonicalised AFTER the spread: schema 8 could not carry a count, so a
      // record that has one is hand-edited and must not keep it.
      return { ...entry, quantity: 1 };
    }),
  };
};

/**
 * 9 -> 10: every Inventory entry gains `held`, at zero.
 *
 * RULE DCC-INV-018. Schema 9 had no way to say "this copy is physically in a
 * hand without being in its Gear Slot", so no schema-9 crawler was holding
 * anything loosely and the honest value is 0 for every entry.
 *
 * **NOTHING IS REINTERPRETED.** `equipped` is not consulted, split or moved.
 * Every existing weapon, shield, helmet and hauberk stays exactly where it was,
 * because a held weapon IS equipped under the one-representation rule and a
 * worn helmet was never in a hand. A migration that tried to guess which
 * equipped copies were "really" being carried would be inventing history.
 *
 * Canonicalised AFTER the spread: schema 9 could not legitimately carry a
 * `held` value, so a record that has one is hand-edited and must not keep it.
 */
const migrate9to10: CharacterMigration = (record) => {
  const inventory = record['inventory'];
  if (!Array.isArray(inventory)) return { ...record, inventory: [] };
  return {
    ...record,
    inventory: inventory.map((entry) => {
      if (typeof entry !== 'object' || entry === null) return entry;
      return { ...(entry as Record<string, unknown>), held: 0 };
    }),
  };
};


/**
 * 10 -> 11: animal companions become a first-class part of a crawler record.
 * Existing crawlers had no companion data to preserve, so they receive an
 * empty list. Pet state is nested with its owner and does not require a D1
 * schema migration.
 */
const migrate10to11: CharacterMigration = (record) => ({ ...record, companions: [] });


/**
 * 11 -> 12: companion progression becomes explicit state. Existing pets keep
 * every prior field and receive neutral defaults for the new GM-managed data.
 * Max Mana follows the ordinary Intelligence rule; no Spell, maturity benefit,
 * or gear assignment is invented during migration.
 */
const migrate11to12: CharacterMigration = (record) => {
  const companions = record['companions'];
  if (!Array.isArray(companions)) return { ...record, companions: [] };
  return {
    ...record,
    companions: companions.map((entry) => {
      if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) return entry;
      const pet = entry as Record<string, unknown>;
      const stats = pet['unenhancedStats'];
      const intValue = typeof stats === 'object' && stats !== null && !Array.isArray(stats)
        ? Number((stats as Record<string, unknown>)['int'])
        : 0;
      return {
        ...pet,
        unspentStatPoints: 0,
        currentMana: Number.isInteger(intValue) && intValue > 0 ? intValue : 0,
        manaBonus: 0,
        skillIds: [],
        spellIds: [],
        matureBenefit: null,
        gearItemIds: [],
      };
    }),
  };
};



/**
 * 12 -> 13: pet Spell assignments may carry a GM-selected Rank override.
 * Existing assigned Spells keep the official default behavior by receiving no
 * override, which means their effective Rank remains the current Floor.
 */
const migrate12to13: CharacterMigration = (record) => {
  const companions = record['companions'];
  if (!Array.isArray(companions)) return { ...record, companions: [] };
  return {
    ...record,
    companions: companions.map((entry) => {
      if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) return entry;
      return { ...(entry as Record<string, unknown>), spellRanks: {} };
    }),
  };
};



/**
 * 13 -> 14: player-owned pet advancement tracks which crawler Level events
 * have already been consumed, and the GM may pre-author custom Level-15
 * maturity choices. Existing pets anchor to the crawler's current Level so an
 * upgrade never grants retroactive pet levels merely because this field is new.
 */
const migrate13to14: CharacterMigration = (record) => {
  const companions = record['companions'];
  if (!Array.isArray(companions)) return { ...record, companions: [] };
  const ownerLevel = Number.isInteger(record['level']) && Number(record['level']) >= 1 ? Number(record['level']) : 1;
  return {
    ...record,
    companions: companions.map((entry) => {
      if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) return entry;
      return {
        ...(entry as Record<string, unknown>),
        ownerLevelLastAdvanced: ownerLevel,
        customMatureBenefits: [],
      };
    }),
  };
};
/** Existing post-tutorial Stats may already include manually allocated points. */
const migrate14to15: CharacterMigration = (record) => ({
  ...record,
  statAdvancement: {
    levelAccountedFor: Number(record['level']) || 1,
    unspentPoints: Number(record['floor']) < 3 ? Math.max(0, (Number(record['level']) - 1) * 3) : 0,
    needsReview: Number(record['floor']) >= 3,
  },
  skillRankLimits: {},
});
export const CHARACTER_MIGRATIONS: Record<number, CharacterMigration> = {
  1: migrate1to2,
  2: migrate2to3,
  3: migrate3to4,
  4: migrate4to5,
  5: migrate5to6,
  6: migrate6to7,
  7: migrate7to8,
  8: migrate8to9,
  9: migrate9to10,
  10: migrate10to11,
  11: migrate11to12,
  12: migrate12to13,
  13: migrate13to14,
  14: migrate14to15,
};

export function migrateCharacterRecord(value: unknown): unknown {
  if (typeof value !== 'object' || value === null) return value;
  const record = { ...(value as Record<string, unknown>) };

  let version = typeof record['schemaVersion'] === 'number' ? record['schemaVersion'] : 0;
  if (version < 1 || version > CHARACTER_SCHEMA_VERSION) return record;

  let current = record;
  while (version < CHARACTER_SCHEMA_VERSION) {
    const step = CHARACTER_MIGRATIONS[version];
    if (!step) throw new Error(`No character migration available from schema version ${version}.`);
    current = step(current);
    version += 1;
    current['schemaVersion'] = version;
  }
  return current;
}
